/**
 * CoverageInquiryPage — the patient-mediated DIN coverage check.
 * See docs/COVERAGE_INQUIRY_DESIGN.md ("Patient screen").
 *
 * Landed on by coverage-purpose magic links (BridgeLanding routes on
 * `purpose === 'coverage'`). Flow:
 *   1. DOB/identity challenge (when the session carries a check hash)
 *   2. Fetch GET /assignments/{aid}/coverage, decrypt client-side with the
 *      symmetric key from the URL fragment (never seen by the bridge)
 *   3. Show drug + DIN + instructions, 3-way answer + insurance details
 *   4. Seal the reply to the Mac's pubkey, POST coverage-reply
 *   5. Done screen
 *
 * The drug name and DIN exist only in the decrypted payload — the bridge
 * stores ciphertext, and the SMS/email that got the patient here carried
 * no drug information.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";

import ParseoLogo from "../../shared/ui/ParseoLogo";
import DobChallenge from "./DobChallenge";
import {
  clearBridgeSession,
  fetchCoverage,
  getBridgeSession,
  sealSubmission,
  submitCoverageReply,
} from "../../lib/bridgeClient";
import {
  buildCoverageReply,
  decodeCoveragePayload,
  insurerContact,
  insurerFieldLabels,
  seedDetailsFromPrefill,
} from "../../lib/coverageInquiry";

const C = {
  navy: "#182a33",
  muted: "#5b6e7c",
  sage: "#6b9c8e",
  sageDark: "#46685d",
  cream: "#f5f0eb",
  line: "#e4dccd",
  faint: "#8a96a3",
  coral: "#c25450",
};

const STAGE = {
  DOB: "dob",
  QUESTION: "question",
  SUBMITTING: "submitting",
  DONE: "done",
  ERROR: "error",
};

/** Decrypted coverage payload — bespoke shape from decodeCoveragePayload. */
interface CoveragePayload {
  inquiry_id?: string;
  drug_name?: string;
  din?: string;
  prefill?: unknown;
  insurer_terms?: unknown;
  insurer_instructions?: {
    steps?: string[];
    app_name?: string;
    web_url?: string | null;
    web_label?: string | null;
    phone?: string | null;
    phone_hours?: string | null;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// Mirrors the (unexported) CoverageDetails in ../../lib/coverageInquiry —
// optional fields + index signature so the values round-trip through
// seedDetailsFromPrefill / buildCoverageReply without the "two unrelated types
// with the same name" clash. Kept structurally identical on purpose.
interface CoverageDetails {
  insurer_name?: string;
  member_id?: string;
  plan_number?: string;
  division_id?: string;
  [key: string]: unknown;
}

export default function CoverageInquiryPage() {
  const session = useMemo(() => getBridgeSession(), []);
  // Server-supplied now — the SPA no longer receives the expected hashes
  // (case imp_bad9596e).
  const hasIdentityChallenge = !!session?.identityRequired;
  const [stage, setStage] = useState<string>(hasIdentityChallenge ? STAGE.DOB : STAGE.QUESTION);
  const [coverage, setCoverage] = useState<CoveragePayload | null>(null); // decrypted payload
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null); // covered|pa_required|not_covered|unsure
  const [details, setDetails] = useState<CoverageDetails>({
    insurer_name: "",
    member_id: "",
    plan_number: "",
    division_id: "",
  });
  const [copied, setCopied] = useState(false);

  // Fetch + decrypt the coverage payload once the SPA has a session. Only
  // ciphertext crosses the wire; the symmetric key came from the magic
  // link's URL fragment and never reached the bridge.
  useEffect(() => {
    if (!session?.sessionToken || !session?.assignmentId) return;
    let cancelled = false;
    fetchCoverage(session.assignmentId)
      .then((body) => {
        if (cancelled) return;
        if (!body?.encrypted_coverage_b64 || !session.symmetricKeyB64) {
          throw new Error("coverage payload missing");
        }
        const payload = decodeCoveragePayload(
          body.encrypted_coverage_b64,
          session.symmetricKeyB64
        );
        setCoverage(payload);
        // Prefill from the patient's file — but patient-typed values always
        // win, so only empty fields are seeded.
        setDetails((prev) => seedDetailsFromPrefill(prev, payload.prefill));
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(
          "Couldn't load your coverage question. Try the link from your text or email again in a moment."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [session?.sessionToken, session?.assignmentId]);

  // No bridge session (never redeemed, or expired) → back to the root,
  // same as BridgeOnboardingPage. The magic link itself is re-redeemable,
  // and a lapsed link offers the renew page from BridgeLanding.
  if (!session) {
    return <Navigate to="/" replace />;
  }

  async function copyDin(din: string | undefined) {
    try {
      await navigator.clipboard.writeText(din as string);
      setCopied(true);
    } catch {
      // Older mobile browsers: hidden-textarea fallback.
      try {
        const ta = document.createElement("textarea");
        ta.value = din as string;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
        setCopied(true);
      } catch {
        return;
      }
    }
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleSubmit() {
    if (!outcome || !coverage) return;
    setStage(STAGE.SUBMITTING);
    try {
      const reply = buildCoverageReply({
        inquiryId: coverage.inquiry_id,
        bridgeAssignmentId: session!.assignmentId,
        outcome,
        details,
      });
      const ciphertextB64 = sealSubmission(reply, session!.pubkeyB64 as string);
      await submitCoverageReply(session!.assignmentId as string, ciphertextB64);
      clearBridgeSession();
      setStage(STAGE.DONE);
    } catch (err) {
      // Duplicate reply (patient re-tapped the link after answering): the
      // answer is already on its way, so show the done screen.
      if ((err as { status?: number })?.status === 409) {
        clearBridgeSession();
        setStage(STAGE.DONE);
        return;
      }
      console.error("coverage reply failed:", err);
      setError((err as Error).message || String(err));
      setStage(STAGE.ERROR);
    }
  }

  if (stage === STAGE.DOB) {
    return (
      <DobChallenge
        // Challenge fields arrive through the bridge session's index signature
        // typed `unknown`; narrow them to what DobChallenge expects.
        identityFactors={session.identityFactors as string[] | undefined}
        onPass={() => setStage(STAGE.QUESTION)}
        onFail={() => {
          // The DobChallenge has already cleared the session; just rerender.
        }}
      />
    );
  }

  if (stage === STAGE.SUBMITTING) {
    return (
      <Centered>
        <ParseoLogo animated size={72} />
        <h2 style={{ color: C.navy, margin: "16px 0 6px" }}>Sending your answer…</h2>
        <p style={{ color: C.muted, margin: 0, maxWidth: 420 }}>
          Encrypting and submitting securely.
        </p>
      </Centered>
    );
  }

  if (stage === STAGE.DONE) {
    return (
      <Centered>
        <ParseoLogo size={56} />
        <h2 style={{ color: C.navy, margin: "16px 0 4px" }}>
          Thanks — we've let your care team know.
        </h2>
        <p style={{ color: C.muted, maxWidth: 440, lineHeight: 1.5, margin: 0 }}>
          If a form is needed, you'll get a separate secure link.
        </p>
      </Centered>
    );
  }

  if (stage === STAGE.ERROR) {
    return (
      <Centered>
        <h2 style={{ color: C.coral, marginBottom: 8 }}>Couldn't send your answer</h2>
        <p style={{ color: C.muted, maxWidth: 420, lineHeight: 1.5 }}>
          Something went wrong. Please try again from the link in your text or
          email, or contact your clinician's office.
        </p>
        {error && (
          <p style={{ color: "#999", fontSize: 12, maxWidth: 420 }}>Detail: {error}</p>
        )}
      </Centered>
    );
  }

  // STAGE.QUESTION
  if (loadError) {
    return (
      <Centered>
        <ParseoLogo size={48} />
        <h2 style={{ color: C.coral, margin: "14px 0 6px" }}>Something went wrong</h2>
        <p style={{ color: C.muted, maxWidth: 420, lineHeight: 1.5 }}>{loadError}</p>
      </Centered>
    );
  }
  if (!coverage) {
    return (
      <Centered>
        <ParseoLogo animated size={72} />
        <h2 style={{ color: C.navy, margin: "16px 0 6px" }}>Loading…</h2>
      </Centered>
    );
  }

  const { memberIdLabel, planLabel } = insurerFieldLabels(coverage.insurer_terms as any);
  const contact = insurerContact(coverage.insurer_instructions);
  const linkBtn: React.CSSProperties = {
    display: "inline-block",
    padding: "9px 14px",
    borderRadius: 8,
    border: `1px solid ${C.sage}`,
    background: "#fff",
    color: C.sageDark,
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
  };
  const answerBtn = (selected: boolean): React.CSSProperties => ({
    width: "100%",
    padding: "13px 16px",
    borderRadius: 10,
    border: selected ? `2px solid ${C.sage}` : `1px solid ${C.line}`,
    background: selected ? "#eef4f2" : "#fff",
    color: selected ? C.sageDark : C.navy,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    textAlign: "left",
    marginBottom: 10,
  });
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "11px 12px",
    fontSize: 15,
    border: `1px solid ${C.line}`,
    borderRadius: 8,
    backgroundColor: "white",
    color: C.navy,
    boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 12.5,
    fontWeight: 600,
    color: C.navy,
    marginBottom: 5,
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: C.cream, padding: "32px 16px" }}>
      <div
        style={{
          maxWidth: 560,
          margin: "0 auto",
          backgroundColor: "#fff",
          borderRadius: 14,
          padding: "26px 22px",
          border: `1px solid ${C.line}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <ParseoLogo size={34} />
          <div style={{ fontSize: 17, fontWeight: 700, color: C.navy, lineHeight: 1.3 }}>
            Does your drug plan cover this medication?
          </div>
        </div>

        {/* Drug card — tap-to-copy DIN */}
        <div
          style={{
            background: C.cream,
            border: `1px solid ${C.line}`,
            borderRadius: 10,
            padding: "14px 16px",
            marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, color: C.navy }}>
            {coverage.drug_name}
          </div>
          {coverage.din && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
              <span style={{ fontSize: 14, color: C.muted, letterSpacing: 0.4 }}>
                DIN {coverage.din}
              </span>
              <button
                onClick={() => copyDin(coverage.din)}
                style={{
                  padding: "4px 12px",
                  borderRadius: 6,
                  border: `1px solid ${copied ? C.sage : C.line}`,
                  background: copied ? "#eef4f2" : "#fff",
                  color: copied ? C.sageDark : C.navy,
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {copied ? "Copied ✓" : "Copy"}
              </button>
            </div>
          )}
        </div>

        {/* Instructions — insurer-specific steps when the payload carries them
            (e.g. GreenShield+ → Coverage → Drug → Calculate Eligibility),
            else the generic fallback. */}
        {coverage.insurer_instructions?.steps?.length ? (
          <>
            {coverage.insurer_instructions.app_name && (
              <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 6 }}>
                How to check in {coverage.insurer_instructions.app_name}:
              </div>
            )}
            {/* Where to go: the member site and the member phone line, when
                the registry could confirm them; otherwise the drug card. */}
            <div style={{ marginBottom: 12 }}>
              {(contact.webUrl || contact.telHref) && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {contact.webUrl && (
                    <a href={contact.webUrl} target="_blank" rel="noopener noreferrer" style={linkBtn}>
                      Open {contact.webLabel} ↗
                    </a>
                  )}
                  {contact.telHref && (
                    <a href={contact.telHref} style={linkBtn}>
                      Call {contact.phone}
                    </a>
                  )}
                </div>
              )}
              {contact.telHref ? (
                contact.phoneHours && (
                  <div style={{ fontSize: 12.5, color: C.muted, marginTop: 6 }}>
                    Phone line: {contact.phoneHours}.
                  </div>
                )
              ) : (
                <div style={{ fontSize: 12.5, color: C.muted, marginTop: contact.webUrl ? 6 : 0 }}>
                  You can also call the number on your drug card.
                </div>
              )}
            </div>
            <ol style={{ margin: "0 0 16px", paddingLeft: 22, color: C.navy }}>
              {coverage.insurer_instructions.steps.map((step, i) => (
                <li key={i} style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 6 }}>
                  {step}
                </li>
              ))}
            </ol>
          </>
        ) : (
          <ol style={{ margin: "0 0 16px", paddingLeft: 22, color: C.navy }}>
            <li style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 6 }}>
              Open your insurance provider's app or website (or call the number on
              your drug card).
            </li>
            <li style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 6 }}>
              Look for "drug coverage" or "coverage search" and enter the DIN above.
            </li>
            <li style={{ fontSize: 14, lineHeight: 1.5 }}>Tell us what you find:</li>
          </ol>
        )}

        {/* Answers */}
        <button onClick={() => setOutcome("covered")} style={answerBtn(outcome === "covered")}>
          Yes — it's covered
        </button>
        <button
          onClick={() => setOutcome("pa_required")}
          style={answerBtn(outcome === "pa_required")}
        >
          It needs an approval form
          <div style={{ fontSize: 12, fontWeight: 400, color: C.muted, marginTop: 3 }}>
            (sometimes called "prior authorization" or "special authorization")
          </div>
        </button>
        <button
          onClick={() => setOutcome("not_covered")}
          style={answerBtn(outcome === "not_covered")}
        >
          No — it's not covered
        </button>
        <button
          onClick={() => setOutcome("unsure")}
          style={{
            background: "none",
            border: "none",
            padding: "2px 4px",
            marginBottom: 6,
            fontSize: 13.5,
            color: outcome === "unsure" ? C.sageDark : C.muted,
            fontWeight: outcome === "unsure" ? 700 : 400,
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          I couldn't find out →
        </button>

        {/* Insurance details */}
        <div style={{ borderTop: `1px solid ${C.line}`, marginTop: 14, paddingTop: 14 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: C.navy, marginBottom: 10 }}>
            Your insurance details (pre-filled from your file — please confirm):
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Insurance provider</label>
            <input
              type="text"
              value={details.insurer_name}
              onChange={(e) => setDetails({ ...details, insurer_name: e.target.value })}
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>{memberIdLabel}</label>
            <input
              type="text"
              value={details.member_id}
              onChange={(e) => setDetails({ ...details, member_id: e.target.value })}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>{planLabel}</label>
            <input
              type="text"
              value={details.plan_number}
              onChange={(e) => setDetails({ ...details, plan_number: e.target.value })}
              style={inputStyle}
            />
            {!details.plan_number && (
              <p style={{ fontSize: 12, color: C.muted, margin: "5px 2px 0" }}>
                Adding your {planLabel.toLowerCase()} (from your drug card) helps us
                confirm coverage for your specific plan.
              </p>
            )}
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={!outcome}
          style={{
            width: "100%",
            marginTop: 18,
            padding: "13px 16px",
            fontSize: 16,
            fontWeight: 600,
            border: "none",
            borderRadius: 10,
            backgroundColor: outcome ? C.navy : "#bbb",
            color: "white",
            cursor: outcome ? "pointer" : "not-allowed",
          }}
        >
          Send answer
        </button>
        {!outcome && (
          <p style={{ fontSize: 12, color: C.faint, textAlign: "center", margin: "8px 0 0" }}>
            Pick one of the answers above to send.
          </p>
        )}
      </div>
    </div>
  );
}

function Centered({ children }: { children?: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: C.cream,
        padding: 24,
        textAlign: "center",
      }}
    >
      {children}
    </div>
  );
}
